import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api'; // Adjust path as needed
import DataTable from '../common/DataTable'; // Assuming a DataTable component exists
import { exportToCsv } from '../../utils/helpers';
import { useAppContext } from '../../context/AppContext';

const ProgramChildren = ({ connectorId }) => {
  const { t } = useAppContext();
  const [childrenData, setChildrenData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    total_pages: 0,
    sort_by: 'gameDate',
    sort_order: 'ASC',
  });
  const [isExporting, setIsExporting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchChildren = async () => {
      if (!connectorId) {
        setError('Connector ID is missing.');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const params = {
          connectorid: connectorId,
          page: pagination.page,
          limit: pagination.limit,
          sort_by: pagination.sort_by,
          sort_order: pagination.sort_order,
        };
        const data = await apiService.getChildren(params); // We'll add this API call next
        setChildrenData(data.programs || []);
        setPagination(data.pagination || { ...pagination, total: 0, total_pages: 0 });
      } catch (err) {
        console.error('Failed to fetch children programs:', err);
        setError(err.message || 'Failed to load children programs.');
        setChildrenData([]); // Set empty array on error
      } finally {
        setIsLoading(false);
      }
    };

    fetchChildren();
  }, [connectorId, pagination.page, pagination.limit, pagination.sort_by, pagination.sort_order]); // Removed pagination from dependencies to prevent infinite loop

  const handleRowClick = (row) => {
    if (row.TMSId) {
      navigate(`/program/${row.TMSId}`);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleSortChange = (newSortBy, newSortOrder) => {
    setPagination(prev => ({
      ...prev,
      page: 1, // Reset to first page on sort change
      sort_by: newSortBy,
      sort_order: newSortOrder,
    }));
  };

  // Define columns for the DataTable based on the API response structure
  const columns = [
    { header: t('programChildren.columns.tmsId'), accessor: 'TMSId', sortable: true },
    { header: t('programChildren.columns.title'), accessor: 'title', sortable: true },
    { header: t('programChildren.columns.gameDate'), accessor: 'gameDate', sortable: true },
    { header: t('programChildren.columns.gameTime'), accessor: 'gameTime', sortable: true },
    { header: t('programChildren.columns.timeZone'), accessor: 'gameTimeZone', sortable: true },
    { header: t('programChildren.columns.venue'), accessor: 'venue', sortable: true },
    { header: t('programChildren.columns.homeTeam'), accessor: 'home_team.name', sortable: true, render: (teamName) => teamName || '-' }, // Handle null team
    { header: t('programChildren.columns.awayTeam'), accessor: 'away_team.name', sortable: true, render: (teamName) => teamName || '-' }, // Handle null team
    { header: t('programChildren.columns.season'), accessor: 'season', sortable: true },
    { header: t('programChildren.columns.seasonType'), accessor: 'season_type', sortable: true },
    { header: t('programChildren.columns.playoffRound'), accessor: 'playoffRound', sortable: true },
    { header: t('programChildren.columns.playoffGame'), accessor: 'playoffRound_gameNum', sortable: true },
    { header: t('programChildren.columns.rootId'), accessor: 'rootId', sortable: true },
    { header: t('programChildren.columns.connectorId'), accessor: 'connectorId', sortable: true },
    { header: t('programChildren.columns.origAirDate'), accessor: 'origAirDate', sortable: true },
    { header: t('programChildren.columns.lang'), accessor: 'lang', sortable: true },
  ];

  if (isLoading) {
    return <div className="p-6 text-center">{t('programChildren.loading')}</div>;
  }

  if (error) {
    return <div className="p-6 text-center text-red-600">{t('programChildren.error', { error })}</div>;
  }

  if (!childrenData || childrenData.length === 0) {
    return <div className="p-6 text-center text-gray-500">{t('programChildren.noChildren')}</div>;
  }

  const handleExportChildren = async () => {
    if (!connectorId) return;

    try {
      setIsExporting(true);
      // Use backend export endpoint to fetch the full dataset (no pagination)
      const allChildren = await apiService.exportChildren(
        connectorId,
        pagination.sort_by,
        pagination.sort_order
      );

      if (!allChildren || allChildren.length === 0) {
        setIsExporting(false);
        return;
      }

      const rows = allChildren.map((child) => ({
        [t('programChildren.columns.tmsId')]: child.TMSId || '',
        [t('programChildren.columns.title')]: child.title || '',
        [t('programChildren.columns.gameDate')]: child.gameDate || '',
        [t('programChildren.columns.gameTime')]: child.gameTime || '',
        [t('programChildren.columns.timeZone')]: child.gameTimeZone || '',
        [t('programChildren.columns.venue')]: child.venue || '',
        [t('programChildren.columns.homeTeam')]: (child.home_team && child.home_team.name) || '',
        [t('programChildren.columns.awayTeam')]: (child.away_team && child.away_team.name) || '',
        [t('programChildren.columns.season')]: child.season || '',
        [t('programChildren.columns.seasonType')]: child.season_type || '',
        [t('programChildren.columns.playoffRound')]: child.playoffRound || '',
        [t('programChildren.columns.playoffGame')]: child.playoffRound_gameNum || '',
        [t('programChildren.columns.rootId')]: child.rootId || '',
        [t('programChildren.columns.connectorId')]: child.connectorId || '',
        [t('programChildren.columns.origAirDate')]: child.origAirDate || '',
        [t('programChildren.columns.lang')]: child.lang || '',
      }));

      exportToCsv(rows, `${connectorId}_children_programs.csv`);
    } catch (err) {
      console.error('Failed to export children programs:', err);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="p-4">
      <div className="mb-4 flex justify-end">
        <button
          className={`px-3 py-1 border border-gray-300 rounded-sm bg-white text-gray-700 hover:bg-gray-100 text-xs ${isExporting ? 'opacity-60 cursor-not-allowed' : ''}`}
          onClick={handleExportChildren}
          disabled={isExporting}
        >
          {isExporting ? t('programChildren.exporting') : t('programChildren.exportList')}
        </button>
      </div>
      <DataTable
        columns={columns}
        data={childrenData}
        pagination={pagination}
        onPageChange={handlePageChange}
        onSortChange={handleSortChange}
        onRowClick={handleRowClick} // Enable row clicking
        defaultSortColumn="gameDate"
        defaultSortOrder="ASC"
      />
    </div>
  );
};

export default ProgramChildren;
